import { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowRight, Bot, Briefcase, Leaf, Sprout, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import Modal from '../shared/Modal';
import ContactForm from '../forms/ContactForm';
import { ROBOTICS_PRODUCTS } from '../../lib/roboticsProducts';

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.45 },
};

const TAGS = ['SELF Labs Robotics', 'Sustainable Agriculture', 'GameFi'];

const HUMANOID_AG_POINTS = [
  'Human-scale reach inside vertical racks, grow aisles, and confined Growbox layouts',
  'Dexterous hands for pruning, harvesting delicate produce, and plant-by-plant inspection',
  'Full-body mobility between grow zones, processing areas, and charging stations',
  'Onboard perception and AI compute for crop monitoring, routing, and task execution',
  'Remote operation and agent-driven workflows through Cultivate Coin player roles',
];

const ROBOT_AG_USE: Record<string, string> = {
  'self-bot-x1':
    'Flagship humanoid for enterprise Growbox deployments — 40 DOF, dexterous manipulation, LiDAR and RGB-D sensing, and NVIDIA Jetson Orin compute for perception-heavy crop workflows.',
  'self-bot-p1':
    'Professional humanoid for pilot programs and regional Growbox networks — full-size mobility and software-ready control at a more accessible tier for scaling agricultural robotics.',
  'self-dog-p1':
    'Quadruped patrol and logistics inside larger facilities — sensor sweeps, supply transport between zones, and infrastructure monitoring alongside humanoid operators.',
  'self-dog-e1':
    'Entry-level quadruped for education, demos, and starter Growbox sites — ideal for training teams and testing autonomous workflows before scaling to full humanoid fleets.',
};

const ROLE_CARDS = [
  {
    icon: Briefcase,
    title: 'The Operators',
    body: 'Operators function as managers for each Growbox, making strategic decisions about deployment locations, expansion rates, and overseeing operations. These players shape the growth and direction of the entire agricultural network, influencing where and how many Growboxes are deployed annually.',
  },
  {
    icon: Sprout,
    title: 'The Farmers',
    body: 'Farmers are the only players with direct control over SELF Labs humanoid robots inside the Growboxes. Through a trivia-based system, they make decisions about plant care (such as when to trim plants or how much water to provide). Correct answers earn Cultivate Coins, while incorrect choices provide educational feedback without rewards, creating a learning-based incentive system.',
  },
  {
    icon: Users,
    title: 'The Negotiators',
    body: 'Negotiators vote on bids from retail partners for the harvested produce. For example, when a Growbox yields 500 pounds of lettuce, Negotiators review and select the highest bid from retail partners. Once accepted, the profits from the sale are added to the Cultivate Coin Liquidity Pool and distributed to all players participating in that specific Growbox\'s operation.',
  },
];

const HUMANOID_PRODUCTS = ROBOTICS_PRODUCTS.filter(
  (p) => p.id === 'self-bot-x1' || p.id === 'self-bot-p1'
);

const HUMANOID_PRODUCT_FILM = encodeURI(
  '/assets/images/SELF Dog P1/_Character_Setup__No_human_subject__The_SELF_humanoid_robot_is_the_sole_subject_of_the_product_film_.mp4'
);

export default function CultivateCoinCaseStudy() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  return (
    <div className="relative min-h-screen bg-black text-white">
      <Helmet>
        <title>Cultivate Coin — SELF Labs</title>
        <meta
          name="description"
          content="Cultivate Coin connects SELF Labs humanoid robotics, sustainable Growbox agriculture, and GameFi — players guide real farming operations through the game."
        />
      </Helmet>

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-indigo-600/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-cyan-600/10 blur-3xl" />
      </div>

      <div className="relative px-4 pb-24 pt-28 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <Link
            to="/case-studies"
            className="group inline-flex items-center text-sm text-white/50 transition-colors hover:text-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            Back to Case Studies
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-10"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
              <Leaf className="h-3.5 w-3.5" />
              SELF Labs Case Study
            </span>

            <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
              Cultivate Coin
              <span className="mt-2 block text-2xl font-medium text-white/55 sm:text-3xl">
                SELF Labs Robotics × GameFi
              </span>
            </h1>

            <div className="mt-6 flex flex-wrap gap-2">
              {TAGS.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70"
                >
                  {tag}
                </span>
              ))}
            </div>

            <p className="mt-6 text-lg leading-relaxed text-white/65">
              Cultivate Coin pairs SELF Labs humanoid and quadruped robots with sustainable Growbox
              agriculture and a role-playing game — connecting players to real-world farming
              operations through embodied AI and gamified decision-making.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="mt-12 overflow-hidden rounded-3xl border border-white/12">
            <img
              src="/assets/images/cultivatecon.png"
              alt="Cultivate Coin sustainable agriculture"
              className="aspect-[16/9] w-full object-cover"
            />
          </motion.div>

          <motion.section {...fadeUp} className="mt-14 border-t border-white/10 pt-14">
            <h2 className="text-2xl font-semibold">Overview</h2>
            <div className="mt-4 space-y-4 text-sm leading-relaxed text-white/65">
              <p>
                Cultivate Coin is a SELF Labs initiative that combines our robotics lineup with
                gamification to revolutionize sustainable agriculture. This case study explores how
                SELF Bot humanoids — deployed inside specialized Growboxes — are guided through an
                innovative gaming platform that turns player decisions into real crop operations.
              </p>
              <p>
                The system bridges virtual gameplay and physical farming. Players participate in
                real-world agricultural operations, from growing produce and flowers to specialized
                cultivation workflows. Actions in Cultivate Coin directly shape what SELF Labs robots
                do on the ground, creating a closed-loop ecosystem for sustainable agriculture and
                community engagement.
              </p>
            </div>
          </motion.section>

          <motion.section {...fadeUp} className="mt-14 border-t border-white/10 pt-14">
            <h2 className="text-2xl font-semibold">Why humanoids for agriculture</h2>
            <p className="mt-4 text-sm leading-relaxed text-white/65">
              Growboxes are built for people — narrow aisles, vertical racks, and tasks that need
              careful hands at human height. SELF Labs humanoid robots are a natural fit: they move
              and work at the same scale as the environment, with the dexterity and perception stack
              to handle delicate agricultural work that quadrupeds and fixed automation cannot.
            </p>

            <div className="mt-8 overflow-hidden rounded-3xl border border-white/12 bg-black">
              <video
                controls
                playsInline
                preload="metadata"
                className="aspect-video w-full bg-black object-contain"
                title="SELF humanoid robot product film"
              >
                <source src={HUMANOID_PRODUCT_FILM} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <p className="border-t border-white/10 px-4 py-3 text-center text-xs text-white/45">
                SELF humanoid robot — product film
              </p>
            </div>

            <ul className="mt-6 space-y-3">
              {HUMANOID_AG_POINTS.map((point) => (
                <li
                  key={point}
                  className="flex gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white/65"
                >
                  <Bot className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
                  {point}
                </li>
              ))}
            </ul>
          </motion.section>

          <motion.section {...fadeUp} className="mt-14 border-t border-white/10 pt-14">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <h2 className="text-2xl font-semibold">SELF Labs robotics for Cultivate Coin</h2>
              <Link
                to="/robotics"
                className="inline-flex items-center gap-1.5 text-sm text-indigo-300 transition hover:text-indigo-200"
              >
                View full robotics catalog
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-8 space-y-4">
              {HUMANOID_PRODUCTS.map((product) => (
                <Link
                  key={product.id}
                  to={`/robotics/${product.id}`}
                  className="group flex flex-col gap-4 overflow-hidden rounded-2xl border border-white/12 bg-white/[0.03] p-4 transition hover:border-white/25 sm:flex-row sm:items-center"
                >
                  <div className="flex h-36 w-full shrink-0 items-center justify-center rounded-xl bg-gradient-to-b from-white/[0.04] to-black sm:h-28 sm:w-40">
                    <img
                      src={product.images[0].src}
                      alt={product.images[0].alt}
                      className="max-h-28 w-full object-contain"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h3 className="text-lg font-semibold group-hover:text-indigo-100">
                        {product.name}
                      </h3>
                      <span className="text-sm font-medium text-white/70">{product.priceLabel}</span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-white/60">
                      {ROBOT_AG_USE[product.id]}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {ROBOTICS_PRODUCTS.filter((p) => p.id.startsWith('self-dog')).map((product) => (
                <Link
                  key={product.id}
                  to={`/robotics/${product.id}`}
                  className="group rounded-2xl border border-white/12 bg-white/[0.03] p-5 transition hover:border-white/25"
                >
                  <h3 className="font-semibold group-hover:text-indigo-100">{product.name}</h3>
                  <p className="mt-1 text-xs text-white/45">{product.priceLabel}</p>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">
                    {ROBOT_AG_USE[product.id]}
                  </p>
                </Link>
              ))}
            </div>
          </motion.section>

          <motion.section {...fadeUp} className="mt-14 border-t border-white/10 pt-14">
            <h2 className="text-2xl font-semibold">The Growbox System</h2>
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-white/12 bg-white/[0.03] p-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.05]">
                    <Leaf className="h-5 w-5 text-indigo-300" />
                  </div>
                  <h3 className="text-lg font-semibold">Sustainable Infrastructure</h3>
                </div>
                <p className="text-sm leading-relaxed text-white/65">
                  Growboxes are approximately 50ft facilities powered by atmospheric water
                  generators, solar panels, and windmills, creating a self-sustaining agricultural
                  environment. This infrastructure allows for year-round cultivation regardless of
                  external conditions, optimizing resource usage and minimizing environmental impact.
                </p>
              </div>

              <div className="rounded-2xl border border-white/12 bg-white/[0.03] p-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.05]">
                    <Bot className="h-5 w-5 text-cyan-300" />
                  </div>
                  <h3 className="text-lg font-semibold">SELF Labs Robotics Integration</h3>
                </div>
                <p className="text-sm leading-relaxed text-white/65">
                  Each Growbox features dedicated charging and staging for SELF Bot humanoids and
                  optional SELF Dog quadrupeds. Humanoids perform the hands-on agricultural work —
                  planting, watering, pruning, and harvesting — guided by Cultivate Coin players.
                  Quadrupeds support patrol, logistics, and sensing across larger deployments.
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-white/12 bg-white/[0.03] p-6 sm:p-8">
              <h3 className="text-lg font-semibold">Distribution Network</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/65">
                Produce grown in Growboxes is distributed through InGrown Organics, which has
                established contracts with major retailers including Whole Foods, Target, Amazon,
                and 13,000 retail locations through partnership with Bangalla. This extensive
                distribution network ensures that sustainably grown produce reaches a wide consumer
                base.
              </p>
            </div>
          </motion.section>

          <motion.section {...fadeUp} className="mt-14 border-t border-white/10 pt-14">
            <h2 className="text-2xl font-semibold">What Is Cultivate Coin?</h2>
            <div className="mt-4 space-y-4 text-sm leading-relaxed text-white/65">
              <p>
                Cultivate Coin is more than a cryptocurrency — it&apos;s a role-playing simulation
                game that connects players to real-world agricultural operations. The game is the
                interface between players, SELF Labs robots, and the Growboxes, allowing virtual
                actions to translate into physical farming activities.
              </p>
              <h3 className="pt-2 text-lg font-semibold text-white/90">How does it work?</h3>
              <p>
                Players participate in the Cultivate Coin ecosystem by answering trivia questions
                and making decisions that affect actual growing operations. The game assigns specific
                roles to players, each with unique responsibilities and rewards. To access the game
                and earn Cultivate Coins, players must own role-specific NFTs in their digital
                wallets.
              </p>
            </div>
          </motion.section>

          <motion.section {...fadeUp} className="mt-14 border-t border-white/10 pt-14">
            <h2 className="text-2xl font-semibold">Player Roles in the Ecosystem</h2>
            <div className="mt-8 space-y-4">
              {ROLE_CARDS.map((role) => {
                const Icon = role.icon;
                return (
                  <div
                    key={role.title}
                    className="flex flex-col gap-4 rounded-2xl border border-white/12 bg-white/[0.03] p-6 sm:flex-row sm:items-start"
                  >
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-indigo-400/30 bg-indigo-500/10">
                      <Icon className="h-6 w-6 text-indigo-300" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-indigo-200/90">{role.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-white/65">{role.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.section>

          <motion.section {...fadeUp} className="mt-14 border-t border-white/10 pt-14">
            <h2 className="text-2xl font-semibold">NFT Passes & Robot Personalization</h2>
            <div className="mt-6 rounded-2xl border border-white/12 bg-white/[0.03] p-6 sm:p-8">
              <p className="text-sm leading-relaxed text-white/65">
                To participate in the Cultivate Coin ecosystem, players must own one of the following
                NFT passes:
              </p>
              <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-white/65">
                <li>Farmer Pass — direct control of SELF Bot humanoids and Growbox operations</li>
                <li>Negotiator Pass — managing marketplace deals and produce distribution</li>
                <li>Operator Pass — strategic expansion and deployment decisions</li>
              </ul>
              <p className="mt-4 text-sm leading-relaxed text-white/65">
                Each NFT pass grants unique permissions within the ecosystem. Farmer passes link
                player profiles to assigned SELF Bots — carrying decision-making preferences,
                agricultural strategies, and resource-management styles into onboard agent workflows
                so robots adapt to how each player tends their Growbox.
              </p>
            </div>
          </motion.section>

          <motion.section {...fadeUp} className="mt-14 border-t border-white/10 pt-14">
            <h2 className="text-2xl font-semibold">Mobile Integration with SELF Phone</h2>
            <div className="mt-6 rounded-2xl border border-white/12 bg-white/[0.03] p-6 sm:p-8">
              <p className="text-sm leading-relaxed text-white/65">
                Cultivate Coin integrates with the SELF Phone so players can manage Growbox
                operations and supervise SELF Labs robots from anywhere — combining mobile
                accessibility with the full SELF ecosystem.
              </p>
              <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-white/65">
                <li>Real-time Growbox monitoring and control</li>
                <li>AI-powered agricultural decision support</li>
                <li>Remote humanoid and quadruped supervision</li>
                <li>Secure blockchain transaction processing</li>
                <li>Live marketplace and trading features</li>
              </ul>
              <p className="mt-4 text-sm leading-relaxed text-white/65">
                With the SELF Phone, farming operations live in your pocket — participate in the
                future of sustainable agriculture from anywhere, at any time.
              </p>
              <Link
                to="/self-phone"
                className="mt-6 inline-flex items-center rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white/80 transition hover:bg-white/10"
              >
                Learn about SELF Phone
              </Link>
            </div>
          </motion.section>

          <motion.section {...fadeUp} className="mt-14 border-t border-white/10 pt-14">
            <h2 className="text-2xl font-semibold">Conclusion</h2>
            <div className="mt-4 space-y-4 text-sm leading-relaxed text-white/65">
              <p>
                Cultivate Coin represents a significant step forward in sustainable agriculture and
                gamified engagement. By connecting virtual gameplay to real-world farming through
                SELF Labs humanoid robotics, the initiative creates a new paradigm for food
                production, distribution, and community participation.
              </p>
              <p>
                Humanoids are the right form factor for Growbox agriculture — and SELF Bot X1 and
                SELF Bot P1 give deployments a clear path from pilot programs to enterprise-scale
                embodied AI. Combined with blockchain incentives and the SELF Phone, Cultivate Coin
                shows how robotics, agents, and player communities can converge on one agricultural
                network.
              </p>
            </div>
          </motion.section>

          <motion.section
            {...fadeUp}
            className="mt-14 rounded-2xl border border-white/12 bg-white/[0.03] p-8 text-center"
          >
            <h3 className="text-xl font-semibold">Ready to join the Cultivate Coin revolution?</h3>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-white/60">
              Mint your Operator, Negotiator, or Farmer Pass — or explore the SELF Labs robots
              powering Growbox operations.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/cultivate-mint"
                className="inline-flex items-center rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 px-6 py-3 text-sm font-semibold text-white transition hover:from-indigo-400 hover:to-cyan-400"
              >
                Mint your pass
              </Link>
              <Link
                to="/robotics"
                className="inline-flex items-center rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-medium text-white/80 transition hover:bg-white/10"
              >
                View robotics
              </Link>
              <button
                type="button"
                onClick={() => setIsContactModalOpen(true)}
                className="inline-flex items-center rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-medium text-white/80 transition hover:bg-white/10"
              >
                Contact us
              </button>
            </div>
          </motion.section>
        </div>
      </div>

      <Modal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        title="Learn More About Cultivate Coin"
      >
        <ContactForm />
      </Modal>
    </div>
  );
}
